import { configureStore, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import api from "app/services/index";
import { DEFAULT_REMINDERS, type ParentProfile, type ReminderPrefs } from "app/services/types";
import storage, { STORAGE_KEYS } from "app/utils/storage";

/* Who is signed in. The profile is kept locally so the app opens instantly; the server re-checks it. */
export const userSlice = createSlice({
  name: "user",
  initialState: { profile: storage.getJson<ParentProfile | null>(STORAGE_KEYS.PROFILE, null), signedIn: Boolean(storage.getItem(STORAGE_KEYS.TOKEN)) },
  reducers: {
    signedIn(state, action: PayloadAction<ParentProfile>) { state.profile = action.payload; state.signedIn = true; storage.setJson(STORAGE_KEYS.PROFILE, action.payload); },
    signedOut(state) { state.profile = null; state.signedIn = false; },
  },
});

/* Reminder choice, per phone (it is the phone that rings). */
export const remindersSlice = createSlice({
  name: "reminders",
  initialState: { prefs: storage.getJson<ReminderPrefs>(STORAGE_KEYS.REMINDERS, DEFAULT_REMINDERS), lastPlannedAt: null as string | null },
  reducers: {
    setPrefs(state, action: PayloadAction<ReminderPrefs>) { state.prefs = action.payload; storage.setJson(STORAGE_KEYS.REMINDERS, action.payload); },
    planned(state, action: PayloadAction<string>) { state.lastPlannedAt = action.payload; },
  },
});

/* A lesson alarm ringing inside the app (web, or the phone app while open). */
export const alarmSlice = createSlice({
  name: "alarm",
  initialState: { ringing: null as null | { registerId: number; title: string; body: string } },
  reducers: {
    ring(state, action: PayloadAction<{ registerId: number; title: string; body: string }>) { state.ringing = action.payload; },
    stop(state) { state.ringing = null; },
  },
});

export const store = configureStore({
  reducer: { [api.reducerPath]: api.reducer, user: userSlice.reducer, reminders: remindersSlice.reducer, alarm: alarmSlice.reducer },
  middleware: (getDefault) => getDefault().concat(api.middleware),
});
setupListeners(store.dispatch);

export type AppStore = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const MAIN_SELECTORS = {
  user: (s: AppStore) => s.user,
  reminders: (s: AppStore) => s.reminders.prefs,
  alarm: (s: AppStore) => s.alarm.ringing,
};
